/**
 * ChatPage Component
 * AIDLC Administrative Backend
 *
 * Module 05: Chat Interface -- THE CROWN JEWEL
 *
 * This is the most critical UI for the CEO workflow:
 *   1. Select an agent from the sidebar
 *   2. View/send messages in the conversation thread
 *   3. Click "Go Do It" to compose a prompt from conversation context
 *   4. Review the composed prompt with persona/project context
 *   5. Select priority and project, then queue it
 *
 * Layout: 2-panel design
 *   Left:   Agent/Conversation list (280px)
 *   Center: Message thread (flex)
 *
 * Prompt queue display has moved to Control Room.
 * "Go Do It" button remains here to queue prompts.
 *
 * Chat is read by Admins and Operators only (CHAT-READ-1): anyone else is shown a plain note, and nothing is loaded.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAgents } from '../../hooks/useAgents';
import { useBridge } from '../layout/MainLayout';
import { usePendingApprovals } from '../../hooks/usePendingApprovals';
import { useAuth } from '../../hooks/useAuth';
import { ApprovalsLoadAlert } from '../agents/ApprovalsLoadAlert';
import type { ApprovalDecision } from '../../services/approvalService';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import { Badge } from '../shared/Badge';
import { Button } from '../shared/Button';
import { Modal } from '../shared/Modal';
import { ChatMarkdownRenderer } from './ChatMarkdownRenderer';
import type { Agent } from '../../types/agent';
import type { ChatMessage, Conversation, PromptPriority, QuickChip } from '../../types/chat';
import {
  getConversations,
  getMessages,
  sendMessage,
  executeGoDoIt,
  clearConversation as clearConversationApi,
  deleteConversation as deleteConversationApi,
  archiveConversation as archiveConversationApi,
  unarchiveConversation as unarchiveConversationApi,
} from '../../services/chatService';
import {
  getChips,
  createChip as createChipApi,
  updateChip as updateChipApi,
  deleteChip as deleteChipApi,
} from '../../services/chipService';
import { getAllProjects } from '../../services/projectService';
import type { Project } from '../../types/project';

// ============================================================
// Status Colors
// ============================================================

const agentStatusColors: Record<string, string> = {
  working: 'bg-green-500',
  available: 'bg-ldaja-blue',
  waiting: 'bg-yellow-500',
  away: 'bg-gray-400',
  offline: 'bg-gray-300',
};

// ============================================================
// Approval Request Types & Styling (inline in ChatPage)
// ============================================================

const approvalTypeLabels: Record<string, string> = {
  tool_denied: 'Permission Required',
  action_approval: 'Action Approval',
  direction_needed: 'Direction Needed',
  review_required: 'Review Required',
};

const approvalTypeColors: Record<string, string> = {
  tool_denied: 'bg-red-50 border-red-300 text-red-700',
  action_approval: 'bg-amber-50 border-amber-300 text-amber-700',
  direction_needed: 'bg-blue-50 border-blue-300 text-blue-700',
  review_required: 'bg-purple-50 border-purple-300 text-purple-700',
};

// ============================================================
// ChatPage Component
// ============================================================

/** What someone who may not read chat is told. The server refuses them chat as well. */
export const CHAT_NOT_AVAILABLE_NOTE = 'Chat is available to Admins and Operators.';

export function ChatPage() {
  const { isAdmin, isOperator } = useAuth();
  if (!isAdmin && !isOperator) {
    return (
      <div className="bg-white rounded-xl border border-gray-200" data-testid="chat-not-available">
        <EmptyState title="Chat" description={CHAT_NOT_AVAILABLE_NOTE} />
      </div>
    );
  }
  return <ChatWorkspace />;
}

function ChatWorkspace() {
  // --- State ---
  const { agents, isLoading: agentsLoading } = useAgents(0); // No polling, load once
  const { socket, isConnected } = useBridge();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messageInput, setMessageInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [showGoDoItModal, setShowGoDoItModal] = useState(false);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showArchivedSection, setShowArchivedSection] = useState(false);

  // Chat management state
  const [confirmAction, setConfirmAction] = useState<{ type: 'clear' | 'delete'; conversationId: string; agentName: string } | null>(null);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const headerMenuRef = useRef<HTMLDivElement>(null);

  // Go Do It form state
  const [goDoItSubject, setGoDoItSubject] = useState('');
  const [goDoItPrompt, setGoDoItPrompt] = useState('');
  const [goDoItPriority, setGoDoItPriority] = useState<PromptPriority>('normal');
  const [goDoItProjectId, setGoDoItProjectId] = useState<string>('');
  const [isExecutingGoDoIt, setIsExecutingGoDoIt] = useState(false);

  const [agentTyping, setAgentTyping] = useState(false);

  // Approval state
  const {
    approvals: pendingApprovals,
    loadError: approvalsLoadError,
    loading: approvalsLoading,
    retryLoad: retryApprovalsLoad,
    isResponding: isRespondingToApproval,
    answerErrors: approvalAnswerErrors,
    respond: respondToApproval,
  } = usePendingApprovals();
  const [approvalInstructionText, setApprovalInstructionText] = useState<Record<string, string>>({});

  // Quick chips state
  const [chips, setChips] = useState<QuickChip[]>([]);
  const [showChipPopover, setShowChipPopover] = useState(false);
  const [editingChip, setEditingChip] = useState<QuickChip | null>(null);
  const [chipLabel, setChipLabel] = useState('');
  const [chipPrompt, setChipPrompt] = useState('');
  const [chipScope, setChipScope] = useState<'agent' | 'global'>('agent');
  const [chipMenuId, setChipMenuId] = useState<string | null>(null);
  const chipPopoverRef = useRef<HTMLDivElement>(null);
  const chipMenuRef = useRef<HTMLDivElement>(null);

  // Smart scroll state
  const [newMessageCount, setNewMessageCount] = useState(0);
  const [isUserNearBottom, setIsUserNearBottom] = useState(true);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const selectedConversationRef = useRef<Conversation | null>(null);

  // Keep ref in sync for use in socket callback
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  // --- Derived ---
  const selectedAgent: Agent | null = useMemo(() => {
    if (!selectedConversation) return null;
    return agents.find((a) => a.id === selectedConversation.agentId) ?? null;
  }, [agents, selectedConversation]);

  const activeConversations = useMemo(() => {
    const filtered = conversations.filter((c) => !c.isArchived);
    if (!searchQuery.trim()) return filtered;
    const q = searchQuery.toLowerCase();
    return filtered.filter(
      (c) =>
        c.agentName.toLowerCase().includes(q) ||
        c.lastMessage.toLowerCase().includes(q)
    );
  }, [conversations, searchQuery]);

  const archivedConversations = useMemo(() => {
    return conversations.filter((c) => c.isArchived);
  }, [conversations]);

  const visibleChips = useMemo(() => {
    if (!selectedConversation) return [];
    return chips
      .filter((c) => c.agentId === null || c.agentId === selectedConversation.agentId)
      .sort((a, b) => a.order - b.order);
  }, [chips, selectedConversation]);

  // Approvals filtered to the currently selected agent
  const currentAgentApprovals = useMemo(() => {
    if (!selectedConversation) return [];
    return pendingApprovals.filter((a) => a.agentId === selectedConversation.agentId);
  }, [pendingApprovals, selectedConversation]);

  // Set of agentIds that have pending approvals (for sidebar badge)
  const agentsWithApprovals = useMemo(() => {
    return new Set(pendingApprovals.map((a) => a.agentId));
  }, [pendingApprovals]);

  // --- Close header menu / chip popover / chip menu on outside click ---
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (headerMenuRef.current && !headerMenuRef.current.contains(e.target as Node)) {
        setShowHeaderMenu(false);
      }
      if (chipPopoverRef.current && !chipPopoverRef.current.contains(e.target as Node)) {
        setShowChipPopover(false);
        setEditingChip(null);
      }
      if (chipMenuRef.current && !chipMenuRef.current.contains(e.target as Node)) {
        setChipMenuId(null);
      }
    }
    if (showHeaderMenu || showChipPopover || chipMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showHeaderMenu, showChipPopover, chipMenuId]);

  // --- Load conversations and projects on mount ---
  useEffect(() => {
    async function load() {
      try {
        const [convos, projs, loadedChips] = await Promise.all([
          getConversations(true), // Include archived
          getAllProjects(),
          getChips(),
        ]);
        setConversations(convos);
        setProjects(projs);
        setChips(loadedChips);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load chat data');
      } finally {
        setIsLoadingConversations(false);
      }
    }
    load();
  }, []);

  // --- Load messages when conversation changes ---
  useEffect(() => {
    if (!selectedConversation) {
      setMessages([]);
      return;
    }
    let cancelled = false;
    async function loadMessages() {
      setIsLoadingMessages(true);
      setNewMessageCount(0);
      setIsUserNearBottom(true);
      try {
        const msgs = await getMessages(selectedConversation!.id);
        if (!cancelled) setMessages(msgs);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load messages');
      } finally {
        if (!cancelled) setIsLoadingMessages(false);
      }
    }
    loadMessages();
    return () => { cancelled = true; };
  }, [selectedConversation]);

  // --- Smart auto-scroll: only scroll when user is near bottom ---
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
    setNewMessageCount(0);
    setIsUserNearBottom(true);
  }, []);

  // Scroll to bottom when messages first load
  useEffect(() => {
    if (!isLoadingMessages && messages.length > 0) {
      // Use instant scroll on initial load, smooth on updates
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
        setIsUserNearBottom(true);
      });
    }
  }, [isLoadingMessages]);

  // Track scroll position to detect if user is near bottom
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const threshold = 100; // px from bottom
    const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
    setIsUserNearBottom(nearBottom);
    if (nearBottom) setNewMessageCount(0);
  }, []);

  // Auto-scroll only when user is near bottom; otherwise increment new message counter
  const prevMessagesLenRef = useRef(0);
  useEffect(() => {
    if (messages.length <= prevMessagesLenRef.current) {
      prevMessagesLenRef.current = messages.length;
      return;
    }
    const newCount = messages.length - prevMessagesLenRef.current;
    prevMessagesLenRef.current = messages.length;

    if (isUserNearBottom) {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      });
    } else {
      setNewMessageCount((prev) => prev + newCount);
    }
  }, [messages, isUserNearBottom]);

  // --- WebSocket: Listen for real-time chat messages ---
  useEffect(() => {
    if (!socket) return;

    const handleChatMessage = (data: {
      id: string;
      conversationId: string;
      sender: string;
      senderType: string;
      content: string;
      timestamp: string;
      agentName?: string;
    }) => {
      // Only append if it's for the currently selected conversation
      const currentConv = selectedConversationRef.current;
      if (currentConv && data.conversationId === currentConv.id) {
        setMessages((prev) => {
          // Deduplicate by id
          if (prev.some((m) => m.id === data.id)) return prev;
          // Skip if this is a user message we already optimistically added
          if (data.senderType === 'user' && prev.some((m) => m.content === data.content && m.senderType === 'user' && m.id.startsWith('temp-'))) {
            // Replace the temp message with the real one
            return prev.map((m) =>
              m.content === data.content && m.senderType === 'user' && m.id.startsWith('temp-')
                ? { ...data, conversationId: data.conversationId, senderType: data.senderType as 'user' | 'agent' | 'system' }
                : m
            );
          }
          return [...prev, {
            id: data.id,
            conversationId: data.conversationId,
            sender: data.sender,
            senderType: data.senderType as 'user' | 'agent' | 'system',
            content: data.content,
            timestamp: data.timestamp,
          }];
        });
      }

      // Update conversation list with latest message
      setConversations((prev) => {
        const existing = prev.find((c) => c.id === data.conversationId);
        if (existing) {
          return prev.map((c) =>
            c.id === data.conversationId
              ? { ...c, lastMessage: data.content, lastMessageTimestamp: data.timestamp }
              : c
          );
        }
        // If this is a new collab conversation, add it with collab flag
        if (data.conversationId.startsWith('conv-collab-')) {
          return [{
            id: data.conversationId,
            agentId: data.sender || 'system',
            agentName: data.agentName || 'Collaborative Thread',
            lastMessage: data.content,
            lastMessageTimestamp: data.timestamp,
            unreadCount: currentConv?.id === data.conversationId ? 0 : 1,
            isCollab: true,
            participants: (data as Record<string, unknown>).participants as string[] || [],
          }, ...prev];
        }
        // If this is a new conversation (agent initiated), add it
        if (data.senderType === 'agent' && data.agentName) {
          return [{
            id: data.conversationId,
            agentId: data.sender,
            agentName: data.agentName,
            lastMessage: data.content,
            lastMessageTimestamp: data.timestamp,
            unreadCount: currentConv?.id === data.conversationId ? 0 : 1,
          }, ...prev];
        }
        return prev;
      });
    };

    socket.on('chat:message', handleChatMessage);
    return () => {
      socket.off('chat:message', handleChatMessage);
    };
  }, [socket]);

  // --- Typing indicator: track when selected agent is "working" ---
  useEffect(() => {
    if (!socket) return;

    const handleAgentStatus = (data: { agentId: string; status: string }) => {
      if (!data || !selectedConversation) return;
      // Only care about the agent in the current conversation
      if (data.agentId !== selectedConversation.agentId) return;
      setAgentTyping(data.status === 'working');
    };

    const handleChatFromAgent = (data: { senderType: string; conversationId: string }) => {
      // Clear typing when agent sends a message in this conversation
      if (data.senderType === 'agent' && selectedConversation && data.conversationId === selectedConversation.id) {
        setAgentTyping(false);
      }
    };

    socket.on('agent:status', handleAgentStatus);
    socket.on('chat:message', handleChatFromAgent);
    return () => {
      socket.off('agent:status', handleAgentStatus);
      socket.off('chat:message', handleChatFromAgent);
    };
  }, [socket, selectedConversation]);

  // Initialize typing state from agent's current status when conversation changes
  useEffect(() => {
    if (!selectedConversation) {
      setAgentTyping(false);
      return;
    }
    const agent = agents.find((a) => a.id === selectedConversation.agentId);
    setAgentTyping(agent?.status === 'working');
  }, [selectedConversation?.id, agents]);

  // --- Handle approval response ---
  // The card leaves only when the server accepted the answer (APPROVAL-TOKEN-1); a refusal keeps it with a message.
  const handleApprovalRespond = useCallback(async (requestId: string, decision: ApprovalDecision) => {
    const accepted = await respondToApproval(requestId, decision, approvalInstructionText[requestId] || '');
    if (!accepted) return;
    setApprovalInstructionText((prev) => {
      const next = { ...prev };
      delete next[requestId];
      return next;
    });
  }, [approvalInstructionText, respondToApproval]);

  // --- Start new conversation with an agent ---
  const startNewConversation = useCallback((agent: Agent) => {
    const convId = `conv-${agent.id}`;
    // Check if conversation already exists
    const existing = conversations.find((c) => c.id === convId);
    if (existing) {
      setSelectedConversation(existing);
      setShowNewChatModal(false);
      return;
    }
    // Create a new conversation entry (no messages yet)
    const newConvo: Conversation = {
      id: convId,
      agentId: agent.id,
      agentName: agent.name,
      lastMessage: '',
      lastMessageTimestamp: new Date().toISOString(),
      unreadCount: 0,
    };
    setConversations((prev) => [newConvo, ...prev]);
    setSelectedConversation(newConvo);
    setShowNewChatModal(false);
  }, [conversations]);

  // --- Auto-select from URL search params (e.g. /chat?agent=marcus or /chat?conv=conv-collab-xxx) ---
  useEffect(() => {
    if (agentsLoading || isLoadingConversations) return;

    // Direct conversation ID (for collab threads from notifications)
    const convParam = searchParams.get('conv');
    if (convParam) {
      const conv = conversations.find((c) => c.id === convParam);
      if (conv) {
        setSelectedConversation(conv);
        setSearchParams({}, { replace: true });
        return;
      }
    }

    // Agent ID (for 1:1 conversations)
    const agentParam = searchParams.get('agent');
    if (!agentParam) return;
    const agent = agents.find((a) => a.id === agentParam);
    if (!agent) return;
    setSearchParams({}, { replace: true });
    startNewConversation(agent);
  }, [agentsLoading, isLoadingConversations, agents, conversations, searchParams, setSearchParams, startNewConversation]);

  // --- Send message ---
  const handleSendMessage = useCallback(async () => {
    if (!selectedConversation || !messageInput.trim() || isSending) return;
    const content = messageInput.trim();
    setMessageInput('');
    setIsSending(true);

    // Optimistic update
    const tempMsg: ChatMessage = {
      id: `temp-${Date.now()}`,
      conversationId: selectedConversation.id,
      sender: 'Neo',
      senderType: 'user',
      content,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempMsg]);

    // Force scroll to bottom on own message
    setIsUserNearBottom(true);
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    });

    // Update conversation list immediately
    setConversations((prev) =>
      prev.map((c) =>
        c.id === selectedConversation.id
          ? { ...c, lastMessage: content, lastMessageTimestamp: tempMsg.timestamp }
          : c
      )
    );

    try {
      // Send via REST API (which also writes to bridge file)
      const newMsg = await sendMessage(selectedConversation.id, content);
      // Replace temp message with server-confirmed message
      setMessages((prev) =>
        prev.map((m) => m.id === tempMsg.id ? newMsg : m)
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
      // Remove optimistic update on error
      setMessages((prev) => prev.filter((m) => m.id !== tempMsg.id));
      setMessageInput(content);
    } finally {
      setIsSending(false);
      inputRef.current?.focus();
    }
  }, [selectedConversation, messageInput, isSending]);

  // --- Resend a previous message ---
  const handleResendMessage = useCallback(async (content: string) => {
    if (!selectedConversation || isSending) return;
    setIsSending(true);

    const tempMsg: ChatMessage = {
      id: `temp-${Date.now()}`,
      conversationId: selectedConversation.id,
      sender: 'Neo',
      senderType: 'user',
      content,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempMsg]);

    setConversations((prev) =>
      prev.map((c) =>
        c.id === selectedConversation.id
          ? { ...c, lastMessage: content, lastMessageTimestamp: tempMsg.timestamp }
          : c
      )
    );

    try {
      const newMsg = await sendMessage(selectedConversation.id, content);
      setMessages((prev) =>
        prev.map((m) => m.id === tempMsg.id ? newMsg : m)
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend message');
      setMessages((prev) => prev.filter((m) => m.id !== tempMsg.id));
    } finally {
      setIsSending(false);
    }
  }, [selectedConversation, isSending]);

  // --- Keyboard handler for message input ---
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage]
  );

  // --- Chat management actions ---
  const handleArchive = useCallback(async (convId: string) => {
    try {
      await archiveConversationApi(convId);
      setConversations((prev) => prev.map((c) => c.id === convId ? { ...c, isArchived: true } : c));
      if (selectedConversation?.id === convId) setSelectedConversation(null);
      setShowHeaderMenu(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to archive');
    }
  }, [selectedConversation]);

  const handleUnarchive = useCallback(async (convId: string) => {
    try {
      await unarchiveConversationApi(convId);
      setConversations((prev) => prev.map((c) => c.id === convId ? { ...c, isArchived: false } : c));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unarchive');
    }
  }, []);

  const handleClear = useCallback(async (convId: string) => {
    try {
      await clearConversationApi(convId);
      setMessages([]);
      setConversations((prev) => prev.map((c) => c.id === convId ? { ...c, lastMessage: '' } : c));
      setConfirmAction(null);
      setShowHeaderMenu(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear');
    }
  }, []);

  const handleDelete = useCallback(async (convId: string) => {
    try {
      await deleteConversationApi(convId);
      setConversations((prev) => prev.filter((c) => c.id !== convId));
      if (selectedConversation?.id === convId) {
        setSelectedConversation(null);
        setMessages([]);
      }
      setConfirmAction(null);
      setShowHeaderMenu(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  }, [selectedConversation]);

  // --- "Go Do It" handler ---
  const openGoDoIt = useCallback(() => {
    if (!selectedConversation || !selectedAgent) return;
    // Pre-populate with conversation context
    const recentMessages = messages.slice(-5).map((m) => `${m.sender}: ${m.content}`).join('\n');
    setGoDoItSubject(`Task from conversation with ${selectedAgent.name}`);
    setGoDoItPrompt(recentMessages || 'Review and execute the discussed task.');
    setGoDoItPriority('normal');
    setGoDoItProjectId(selectedAgent.currentProject || '');
    setShowGoDoItModal(true);
  }, [selectedConversation, selectedAgent, messages]);

  const executeGoDoItAction = useCallback(async () => {
    if (!selectedConversation || !goDoItSubject.trim()) return;
    setIsExecutingGoDoIt(true);
    try {
      const result = await executeGoDoIt(selectedConversation.id, {
        toAgent: selectedConversation.agentId,
        priority: goDoItPriority,
        projectId: goDoItProjectId || undefined,
        subject: goDoItSubject,
        prompt: goDoItPrompt,
      });
      setShowGoDoItModal(false);
      // Add system message to chat
      setMessages((prev) => [
        ...prev,
        {
          id: `system-${Date.now()}`,
          conversationId: selectedConversation.id,
          sender: 'System',
          senderType: 'system',
          content: `Prompt queued successfully: "${goDoItSubject}" (Priority: ${goDoItPriority})`,
          timestamp: new Date().toISOString(),
          metadata: { promptLink: result.id },
        },
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to queue prompt');
    } finally {
      setIsExecutingGoDoIt(false);
    }
  }, [selectedConversation, goDoItSubject, goDoItPrompt, goDoItPriority, goDoItProjectId]);

  // --- Chip handlers ---
  const handleChipClick = useCallback((chip: QuickChip, ctrlKey: boolean) => {
    if (ctrlKey) {
      // Ctrl+Click: send immediately (use resend pattern to bypass state timing)
      handleResendMessage(chip.prompt);
    } else {
      // Normal click: populate input
      setMessageInput(chip.prompt);
      inputRef.current?.focus();
    }
  }, [handleResendMessage]);

  const openSaveAsChip = useCallback((messageContent: string) => {
    // Smart label: first 30 chars, truncated at word boundary
    const words = messageContent.split(/\s+/);
    let label = '';
    for (const word of words) {
      if ((label + ' ' + word).trim().length > 30) break;
      label = (label + ' ' + word).trim();
    }
    setChipLabel(label || messageContent.slice(0, 30));
    setChipPrompt(messageContent);
    setChipScope('agent');
    setEditingChip(null);
    setShowChipPopover(true);
  }, []);

  const openCreateChip = useCallback(() => {
    setChipLabel('');
    setChipPrompt('');
    setChipScope('agent');
    setEditingChip(null);
    setShowChipPopover(true);
  }, []);

  const openEditChip = useCallback((chip: QuickChip) => {
    setChipLabel(chip.label);
    setChipPrompt(chip.prompt);
    setChipScope(chip.agentId ? 'agent' : 'global');
    setEditingChip(chip);
    setShowChipPopover(true);
    setChipMenuId(null);
  }, []);

  const handleSaveChip = useCallback(async () => {
    if (!chipLabel.trim() || !chipPrompt.trim()) return;
    const agentId = chipScope === 'agent' && selectedConversation ? selectedConversation.agentId : null;

    if (editingChip) {
      // Update existing
      try {
        const updated = await updateChipApi(editingChip.id, {
          label: chipLabel.trim(),
          prompt: chipPrompt.trim(),
          agentId,
        });
        setChips((prev) => prev.map((c) => c.id === updated.id ? updated : c));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update chip');
      }
    } else {
      // Create new — optimistic
      const tempChip: QuickChip = {
        id: `temp-chip-${Date.now()}`,
        label: chipLabel.trim(),
        prompt: chipPrompt.trim(),
        agentId,
        color: 'blue',
        order: chips.length,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setChips((prev) => [...prev, tempChip]);

      try {
        const created = await createChipApi({
          label: chipLabel.trim(),
          prompt: chipPrompt.trim(),
          agentId,
        });
        setChips((prev) => prev.map((c) => c.id === tempChip.id ? created : c));
      } catch (err) {
        setChips((prev) => prev.filter((c) => c.id !== tempChip.id));
        setError(err instanceof Error ? err.message : 'Failed to create chip');
      }
    }

    setShowChipPopover(false);
    setEditingChip(null);
  }, [chipLabel, chipPrompt, chipScope, selectedConversation, editingChip, chips.length]);

  const handleDeleteChip = useCallback(async (chipId: string) => {
    const prev = chips;
    setChips((c) => c.filter((ch) => ch.id !== chipId));
    setChipMenuId(null);
    try {
      await deleteChipApi(chipId);
    } catch (err) {
      setChips(prev);
      setError(err instanceof Error ? err.message : 'Failed to delete chip');
    }
  }, [chips]);

  // --- Render helpers ---
  const formatTime = (ts: string) => {
    const d = new Date(ts);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // ============================================================
  // RENDER
  // ============================================================

  if (agentsLoading || isLoadingConversations) {
    return <LoadingSpinner message="Loading chat interface..." />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <p className="text-red-700 text-sm font-medium">Chat Error</p>
        <p className="text-red-600 text-xs mt-1">{error}</p>
        <button
          onClick={() => { setError(null); window.location.reload(); }}
          className="mt-3 px-4 py-2 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex bg-gray-50 rounded-lg overflow-hidden border border-gray-200 -m-6" style={{ height: 'calc(100vh - 48px)' }}>

      {/* ============================================================ */}
      {/* LEFT PANEL: Conversations List */}
      {/* ============================================================ */}
      <div className="w-[280px] flex-shrink-0 bg-white border-r border-gray-200 flex flex-col min-h-0">
        {/* Header with New Chat button */}
        <div className="p-3 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-xs font-semibold text-ldaja-black">Messages</h3>
            {isConnected && (
              <span className="flex items-center gap-1 text-[10px] text-green-600">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                Live
              </span>
            )}
          </div>
          <button
            onClick={() => setShowNewChatModal(true)}
            className="p-1.5 text-ldaja-blue hover:bg-ldaja-blue/10 rounded-md transition-colors"
            title="New conversation"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="p-3 border-b border-gray-100">
          <div className="relative">
            <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-ldaja-blue/50"
            />
          </div>
        </div>

        {approvalsLoadError && (
          <ApprovalsLoadAlert
            message={approvalsLoadError}
            onRetry={retryApprovalsLoad}
            retrying={approvalsLoading}
            className="mx-3 mt-3 p-2.5 rounded-md text-xs"
          />
        )}

        {/* Conversation List -- min-h-0 prevents flex-1 from using content
            height as minimum, enabling independent scroll */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {activeConversations.length === 0 && archivedConversations.length === 0 ? (
            <div className="p-4 text-center text-xs text-gray-400">
              No conversations yet
            </div>
          ) : (
            <>
              {/* Active conversations */}
              {activeConversations.map((convo) => (
                <ConversationItem
                  key={convo.id}
                  convo={convo}
                  agents={agents}
                  isSelected={selectedConversation?.id === convo.id}
                  onSelect={() => setSelectedConversation(convo)}
                  onArchive={() => handleArchive(convo.id)}
                  onDelete={() => setConfirmAction({ type: 'delete', conversationId: convo.id, agentName: convo.agentName })}
                  formatTime={formatTime}
                  hasApproval={agentsWithApprovals.has(convo.agentId)}
                />
              ))}

              {/* Archived section */}
              {archivedConversations.length > 0 && (
                <div className="border-t border-gray-200">
                  <button
                    onClick={() => setShowArchivedSection(!showArchivedSection)}
                    className="w-full flex items-center justify-between px-3 py-2 text-xs text-gray-500 hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-medium">Archived ({archivedConversations.length})</span>
                    <svg
                      className={`w-3.5 h-3.5 transition-transform ${showArchivedSection ? 'rotate-180' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                    >
                      <path d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showArchivedSection && (
                    <div className="bg-gray-50/50">
                      {archivedConversations.map((convo) => (
                        <ConversationItem
                          key={convo.id}
                          convo={convo}
                          agents={agents}
                          isSelected={selectedConversation?.id === convo.id}
                          onSelect={() => setSelectedConversation(convo)}
                          onUnarchive={() => handleUnarchive(convo.id)}
                          onDelete={() => setConfirmAction({ type: 'delete', conversationId: convo.id, agentName: convo.agentName })}
                          formatTime={formatTime}
                          isArchived
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ============================================================ */}
      {/* CENTER PANEL: Message Thread */}
      {/* ============================================================ */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {selectedConversation && (selectedAgent || selectedConversation.isCollab) ? (
          <>
            {/* Conversation Header -- flex-shrink-0 keeps it pinned at top */}
            <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200">
              <div className="flex items-center gap-3">
                {selectedConversation.isCollab ? (
                  <>
                    {/* Collab thread: stacked avatars */}
                    <div className="flex -space-x-2">
                      {(selectedConversation.participants || []).map((pid) => {
                        const pa = agents.find((a) => a.id === pid);
                        return (
                          <div
                            key={pid}
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold border-2 border-white"
                            style={{ backgroundColor: pa?.avatarColor || '#6366f1' }}
                            title={pa?.name || pid}
                          >
                            {pa?.name.charAt(0) || '?'}
                          </div>
                        );
                      })}
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-ldaja-black">{selectedConversation.agentName}</h3>
                      <p className="text-xs text-gray-500">Collaborative thread</p>
                    </div>
                    <Badge variant="info">collab</Badge>
                  </>
                ) : selectedAgent ? (
                  <>
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold"
                      style={{ backgroundColor: selectedAgent.avatarColor || '#2563EB' }}
                    >
                      {selectedAgent.name.charAt(0)}
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-ldaja-black">{selectedAgent.name}</h3>
                      <p className="text-xs text-gray-500">{selectedAgent.role}</p>
                    </div>
                    <Badge
                      variant={selectedAgent.status === 'working' ? 'success' : selectedAgent.status === 'available' ? 'info' : selectedAgent.status === 'waiting' ? 'warning' : 'neutral'}
                      dot
                    >
                      {selectedAgent.status}
                    </Badge>
                  </>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                {/* GO DO IT Button -- The Crown Jewel */}
                <Button
                  variant="primary"
                  size="sm"
                  onClick={openGoDoIt}
                  className="bg-green-600 hover:bg-green-700 focus:ring-green-500/50 font-bold"
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Go Do It
                </Button>
                {/* Three-dot menu */}
                <div className="relative" ref={headerMenuRef}>
                  <button
                    onClick={() => setShowHeaderMenu(!showHeaderMenu)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                    title="Conversation actions"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path d="M12 5v.01M12 12v.01M12 19v.01" strokeLinecap="round" />
                    </svg>
                  </button>
                  {showHeaderMenu && (
                    <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50">
                      <button
                        onClick={() => {
                          setConfirmAction({ type: 'clear', conversationId: selectedConversation.id, agentName: selectedConversation.agentName });
                          setShowHeaderMenu(false);
                        }}
                        className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Clear messages
                      </button>
                      <button
                        onClick={() => handleArchive(selectedConversation.id)}
                        className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                        </svg>
                        Archive conversation
                      </button>
                      <hr className="my-1 border-gray-100" />
                      <button
                        onClick={() => {
                          setConfirmAction({ type: 'delete', conversationId: selectedConversation.id, agentName: selectedConversation.agentName });
                          setShowHeaderMenu(false);
                        }}
                        className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete conversation
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Messages Area wrapper -- flex-1 min-h-0 + relative so the
                "new messages" pill can float without disrupting flex layout */}
            <div className="flex-1 min-h-0 relative">
              <div
                ref={messagesContainerRef}
                onScroll={handleScroll}
                className="absolute inset-0 overflow-y-auto px-4 py-4 space-y-4"
              >
                {isLoadingMessages ? (
                  <LoadingSpinner message="Loading messages..." size="sm" />
                ) : messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <p className="text-sm text-gray-500">No messages yet</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Start a conversation with {selectedConversation.agentName}
                      </p>
                    </div>
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isUser = msg.senderType === 'user';
                    const isSystem = msg.senderType === 'system';
                    // In collab threads, look up the agent by sender name; in 1:1, use conversation agentId
                    const msgAgent = !isUser && !isSystem
                      ? (selectedConversation.isCollab
                          ? agents.find((a) => a.name === msg.sender || a.id === msg.sender)
                          : agents.find((a) => a.id === selectedConversation.agentId))
                      : null;

                    if (isSystem) {
                      // Detect collab thread creation system messages
                      const collabMatch = msg.content.match(/^Multi-agent thread created: (.+)\. Conversation continues in the new thread\.$/);
                      if (collabMatch) {
                        const collabThreadId = (msg.metadata as Record<string, unknown> | undefined)?.collabThreadId as string | undefined;
                        return (
                          <div key={msg.id} className="flex justify-center">
                            <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-2.5 max-w-md">
                              <div className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-indigo-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <div className="text-xs text-indigo-600">
                                  <p className="font-semibold">{collabMatch[1]}</p>
                                  {collabThreadId && (
                                    <button
                                      onClick={() => {
                                        const collabConvo = conversations.find((c) => c.id === collabThreadId);
                                        if (collabConvo) setSelectedConversation(collabConvo);
                                      }}
                                      className="underline hover:text-indigo-800 text-[10px] mt-0.5"
                                    >
                                      Open collaborative thread
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      }

                      // Detect agent join messages in collab threads
                      const joinMatch = msg.content.match(/has (been invited|joined) /);
                      if (joinMatch) {
                        return (
                          <div key={msg.id} className="flex justify-center">
                            <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-2 max-w-md">
                              <div className="flex items-center gap-2">
                                <svg className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                </svg>
                                <p className="text-xs text-indigo-600">{msg.content}</p>
                              </div>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div key={msg.id} className="flex justify-center">
                          <div className="bg-ldaja-blue/5 border border-ldaja-blue/20 rounded-lg px-4 py-2 max-w-md">
                            <div className="flex items-center gap-2">
                              <svg className="w-3.5 h-3.5 text-ldaja-blue flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                              <p className="text-xs text-ldaja-blue">{msg.content}</p>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isUser ? 'justify-end' : 'justify-start'} group`}
                      >
                        <div className={`flex gap-2.5 max-w-[75%] ${isUser ? 'flex-row-reverse' : ''}`}>
                          {/* Avatar */}
                          <div
                            className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${
                              isUser ? 'bg-ldaja-black' : ''
                            }`}
                            style={!isUser && msgAgent ? { backgroundColor: msgAgent.avatarColor || '#2563EB' } : undefined}
                          >
                            {isUser ? 'N' : (msgAgent?.name.charAt(0) || 'A')}
                          </div>
                          {/* Message Bubble */}
                          <div>
                            <div className={`flex items-center gap-2 mb-0.5 ${isUser ? 'justify-end' : ''}`}>
                              <span className="text-[10px] font-medium text-gray-500">
                                {msg.sender}
                              </span>
                              <span className="text-[10px] text-gray-400">
                                {formatTime(msg.timestamp)}
                              </span>
                            </div>
                            <div className={`flex items-end gap-1.5 ${isUser ? 'flex-row-reverse' : ''}`}>
                              <div
                                className={`rounded-lg px-3 py-2 text-sm ${
                                  isUser
                                    ? 'bg-ldaja-blue text-white'
                                    : 'bg-white border border-gray-200 text-gray-800'
                                }`}
                              >
                                <ChatMarkdownRenderer content={msg.content} isUser={isUser} />
                              </div>
                              {/* Resend + Save-as-chip buttons -- user messages only */}
                              {isUser && !msg.id.startsWith('temp-') && (
                                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
                                  <button
                                    onClick={() => handleResendMessage(msg.content)}
                                    disabled={isSending}
                                    title="Resend this message"
                                    className="p-1 rounded-md text-gray-400 hover:text-ldaja-blue hover:bg-ldaja-blue/10 disabled:opacity-30"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                      <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => openSaveAsChip(msg.content)}
                                    title="Save as quick action chip"
                                    className="p-1 rounded-md text-gray-400 hover:text-ldaja-cyan hover:bg-ldaja-cyan/10"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                      <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                                    </svg>
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                {/* Typing indicator */}
                {agentTyping && selectedAgent && (
                  <div className="flex items-start gap-3 px-4 py-2">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: selectedAgent.avatarColor || '#2563EB' }}
                    >
                      {selectedAgent.name.charAt(0)}
                    </div>
                    <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3">
                      <div className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* "New messages" pill — absolutely positioned within the
                  messages wrapper so it never disrupts the flex layout */}
              {newMessageCount > 0 && !isUserNearBottom && (
                <button
                  onClick={() => scrollToBottom()}
                  className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-ldaja-blue text-white text-xs font-medium px-4 py-1.5 rounded-full shadow-lg hover:bg-ldaja-blue/90 transition-all animate-bounce z-40 flex items-center gap-1.5"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                  {newMessageCount} new message{newMessageCount > 1 ? 's' : ''}
                </button>
              )}
            </div>

            {/* Inline Approval Cards -- shown above the input when current agent has pending approvals */}
            {currentAgentApprovals.length > 0 && (
              <div className="flex-shrink-0 bg-amber-50/50 border-t border-amber-200 px-4 py-3 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" />
                  </span>
                  <h3 className="text-xs font-semibold text-amber-700">
                    {currentAgentApprovals.length} Pending Approval{currentAgentApprovals.length !== 1 ? 's' : ''}
                  </h3>
                </div>
                {currentAgentApprovals.map((approval) => (
                  <div
                    key={approval.id}
                    className={`border rounded-lg p-3 ${approvalTypeColors[approval.type] || 'bg-gray-50 border-gray-200 text-gray-700'}`}
                  >
                    <div className="flex items-start justify-between mb-1.5">
                      <div>
                        <span className="text-[10px] font-semibold uppercase tracking-wide opacity-70">
                          {approvalTypeLabels[approval.type] || approval.type}
                        </span>
                        <h4 className="text-sm font-semibold mt-0.5">{approval.title}</h4>
                      </div>
                      <span className="text-[10px] opacity-60">
                        {new Date(approval.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    <p className="text-xs opacity-80 mb-2">{approval.description}</p>
                    {approval.context && Object.keys(approval.context).length > 0 && (
                      <pre className="text-[10px] bg-white/50 rounded p-2 mb-2 overflow-x-auto opacity-70">
                        {JSON.stringify(approval.context, null, 2)}
                      </pre>
                    )}
                    <div className="mb-2">
                      <textarea
                        placeholder="Send instructions to the agent (optional)..."
                        value={approvalInstructionText[approval.id] || ''}
                        onChange={(e) => setApprovalInstructionText((prev) => ({ ...prev, [approval.id]: e.target.value }))}
                        rows={2}
                        className="w-full px-2.5 py-1.5 text-xs border border-current/20 rounded-md bg-white/80 focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50 placeholder:opacity-50"
                      />
                    </div>
                    {approvalAnswerErrors[approval.id] && (
                      <p role="alert" className="mb-2 text-xs font-medium text-red-700">
                        {approvalAnswerErrors[approval.id]}
                      </p>
                    )}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleApprovalRespond(approval.id, 'approved')}
                        disabled={isRespondingToApproval(approval.id)}
                        className="px-3 py-1 text-xs font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleApprovalRespond(approval.id, 'denied')}
                        disabled={isRespondingToApproval(approval.id)}
                        className="px-3 py-1 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
                      >
                        Deny
                      </button>
                      {approvalInstructionText[approval.id]?.trim() && (
                        <button
                          onClick={() => handleApprovalRespond(approval.id, 'instructions')}
                          disabled={isRespondingToApproval(approval.id)}
                          className="px-3 py-1 text-xs font-medium text-white bg-ldaja-blue rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                        >
                          Send Instructions
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Quick-Action Chips + Message Input -- flex-shrink-0 keeps it pinned at bottom */}
            <div className="flex-shrink-0 bg-white border-t border-gray-200">
              {/* Chip Tray */}
              <div className="px-4 pt-2 pb-0 relative">
                <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                  {visibleChips.length === 0 ? (
                    <span className="text-[10px] text-gray-400">No quick actions yet</span>
                  ) : (
                    <>
                      {visibleChips.map((chip) => {
                        const isAgentSpecific = chip.agentId !== null;
                        return (
                          <div key={chip.id} className="relative flex-shrink-0">
                            <button
                              onClick={(e) => handleChipClick(chip, e.ctrlKey || e.metaKey)}
                              onContextMenu={(e) => {
                                e.preventDefault();
                                setChipMenuId(chipMenuId === chip.id ? null : chip.id);
                              }}
                              title={chip.prompt.length > 120 ? chip.prompt.slice(0, 120) + '...' : chip.prompt}
                              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors duration-150 border
                                ${isAgentSpecific
                                  ? 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-ldaja-blue/10 hover:text-ldaja-blue hover:border-ldaja-blue/30 border-l-2 border-l-ldaja-cyan'
                                  : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-ldaja-blue/10 hover:text-ldaja-blue hover:border-ldaja-blue/30'
                                } active:bg-ldaja-blue active:text-white`}
                            >
                              {chip.label}
                            </button>
                            {/* Chip context menu */}
                            {chipMenuId === chip.id && (
                              <div ref={chipMenuRef} className="absolute left-0 top-full mt-1 w-36 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50">
                                <button
                                  onClick={() => openEditChip(chip)}
                                  className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteChip(chip.id)}
                                  className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50"
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </>
                  )}
                  {/* [+] Add chip button — always visible */}
                  <button
                    onClick={openCreateChip}
                    className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full border border-dashed border-gray-300 text-gray-400 hover:border-ldaja-blue hover:text-ldaja-blue transition-colors"
                    title="Add quick action"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>

                {/* Chip Create/Edit Popover */}
                {showChipPopover && (
                  <div ref={chipPopoverRef} className="absolute left-4 bottom-full mb-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50">
                    <h4 className="text-xs font-semibold text-ldaja-black mb-3">
                      {editingChip ? 'Edit Quick Action' : 'Save as Quick Action'}
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-[10px] font-medium text-gray-600 mb-1">Label (max 30 chars)</label>
                        <input
                          type="text"
                          value={chipLabel}
                          onChange={(e) => setChipLabel(e.target.value.slice(0, 30))}
                          placeholder="e.g. Review Auth Module"
                          className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-ldaja-blue/50"
                          autoFocus
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-gray-600 mb-1">Prompt</label>
                        <textarea
                          value={chipPrompt}
                          onChange={(e) => setChipPrompt(e.target.value)}
                          rows={3}
                          placeholder="The message sent to the agent when clicked..."
                          className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-ldaja-blue/50"
                        />
                      </div>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                          <input
                            type="radio"
                            name="chipScope"
                            checked={chipScope === 'agent'}
                            onChange={() => setChipScope('agent')}
                            className="text-ldaja-blue focus:ring-ldaja-blue"
                          />
                          This agent only
                        </label>
                        <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                          <input
                            type="radio"
                            name="chipScope"
                            checked={chipScope === 'global'}
                            onChange={() => setChipScope('global')}
                            className="text-ldaja-blue focus:ring-ldaja-blue"
                          />
                          All agents
                        </label>
                      </div>
                      <div className="flex justify-end gap-2 pt-1">
                        <button
                          onClick={() => { setShowChipPopover(false); setEditingChip(null); }}
                          className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveChip}
                          disabled={!chipLabel.trim() || !chipPrompt.trim()}
                          className="px-3 py-1.5 text-xs text-white bg-ldaja-blue hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {editingChip ? 'Update' : 'Save'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Message Input */}
              <div className="px-4 py-3">
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <textarea
                      ref={inputRef}
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder={`Message ${selectedAgent?.name || selectedConversation.agentName}...`}
                      rows={1}
                      className="w-full resize-none px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50 focus:border-ldaja-blue"
                      style={{ minHeight: '38px', maxHeight: '120px' }}
                    />
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSendMessage}
                    disabled={!messageInput.trim() || isSending}
                    isLoading={isSending}
                    className="self-end"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  </Button>
                </div>
                <p className="text-[10px] text-gray-400 mt-1">
                  Press Enter to send, Shift+Enter for new line | Click chip to fill, Ctrl+Click to send instantly
                </p>
              </div>
            </div>
          </>
        ) : (
          /* No conversation selected */
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <EmptyState
              title="Select a conversation"
              description="Choose an agent conversation from the sidebar to start chatting and using the Go Do It prompt system."
            />
          </div>
        )}
      </div>

      {/* ============================================================ */}
      {/* CONFIRMATION MODAL -- Clear / Delete */}
      {/* ============================================================ */}
      <Modal
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        title={confirmAction?.type === 'clear' ? 'Clear Messages' : 'Delete Conversation'}
        size="sm"
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setConfirmAction(null)}>
              Cancel
            </Button>
            <Button
              variant={confirmAction?.type === 'delete' ? 'danger' : 'primary'}
              size="sm"
              onClick={() => {
                if (!confirmAction) return;
                if (confirmAction.type === 'clear') handleClear(confirmAction.conversationId);
                else handleDelete(confirmAction.conversationId);
              }}
            >
              {confirmAction?.type === 'clear' ? 'Clear Messages' : 'Delete'}
            </Button>
          </>
        }
      >
        {confirmAction?.type === 'clear' ? (
          <p className="text-sm text-gray-600">
            Clear all messages in your conversation with <span className="font-semibold">{confirmAction.agentName}</span>?
            The conversation will remain in your list but all messages will be permanently removed.
          </p>
        ) : (
          <p className="text-sm text-gray-600">
            Permanently delete your conversation with <span className="font-semibold">{confirmAction?.agentName}</span>?
            All messages will be lost. This cannot be undone.
          </p>
        )}
      </Modal>

      {/* ============================================================ */}
      {/* GO DO IT MODAL */}
      {/* ============================================================ */}
      <Modal
        isOpen={showGoDoItModal}
        onClose={() => setShowGoDoItModal(false)}
        title="Go Do It -- Queue Prompt"
        size="lg"
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setShowGoDoItModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={executeGoDoItAction}
              isLoading={isExecutingGoDoIt}
              disabled={!goDoItSubject.trim()}
              className="bg-green-600 hover:bg-green-700 focus:ring-green-500/50 font-bold"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Queue Prompt
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {/* Agent Info Banner */}
          {selectedAgent && (
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold"
                style={{ backgroundColor: selectedAgent.avatarColor || '#2563EB' }}
              >
                {selectedAgent.name.charAt(0)}
              </div>
              <div>
                <p className="text-sm font-medium text-ldaja-black">{selectedAgent.name}</p>
                <p className="text-xs text-gray-500">{selectedAgent.role}</p>
              </div>
              <div className="ml-auto text-right">
                <p className="text-[10px] text-gray-400">Expertise</p>
                <p className="text-xs text-gray-600">{selectedAgent.expertise.slice(0, 2).join(', ')}</p>
              </div>
            </div>
          )}

          {/* Subject */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Subject</label>
            <input
              type="text"
              value={goDoItSubject}
              onChange={(e) => setGoDoItSubject(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50 focus:border-ldaja-blue"
              placeholder="Brief description of the task..."
            />
          </div>

          {/* Priority + Project Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={goDoItPriority}
                onChange={(e) => setGoDoItPriority(e.target.value as PromptPriority)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Project (optional)</label>
              <select
                value={goDoItProjectId}
                onChange={(e) => setGoDoItProjectId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
              >
                <option value="">No project</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Prompt Content */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Prompt Context
              <span className="text-gray-400 font-normal ml-1">(auto-populated from conversation)</span>
            </label>
            <textarea
              value={goDoItPrompt}
              onChange={(e) => setGoDoItPrompt(e.target.value)}
              rows={8}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50 focus:border-ldaja-blue font-mono"
              placeholder="The full prompt context that will be composed with persona and project context..."
            />
            <p className="text-[10px] text-gray-400 mt-1">
              The backend will automatically inject {selectedAgent?.name || 'agent'}&apos;s persona context and any project context.
            </p>
          </div>

          {/* Context Preview */}
          <details className="border border-gray-200 rounded-lg">
            <summary className="px-3 py-2 text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-50">
              Preview composed context
            </summary>
            <div className="px-3 py-2 text-xs text-gray-500 font-mono border-t border-gray-100 bg-gray-50 max-h-40 overflow-y-auto whitespace-pre-wrap">
              {selectedAgent && (
                <>
                  {'=== PERSONA CONTEXT ===\n'}
                  {`Agent: ${selectedAgent.name} (${selectedAgent.fullName})\n`}
                  {`Role: ${selectedAgent.role}\n`}
                  {`Expertise: ${selectedAgent.expertise.join(', ')}\n`}
                  {selectedAgent.catchphrase ? `Style: "${selectedAgent.catchphrase}"\n` : ''}
                  {'\n=== TASK ===\n'}
                  {`Subject: ${goDoItSubject}\n`}
                  {`Priority: ${goDoItPriority}\n`}
                  {'\n=== CONVERSATION CONTEXT ===\n'}
                  {goDoItPrompt}
                </>
              )}
            </div>
          </details>
        </div>
      </Modal>

      {/* ============================================================ */}
      {/* NEW CHAT MODAL -- Select agent to start conversation */}
      {/* ============================================================ */}
      <Modal
        isOpen={showNewChatModal}
        onClose={() => setShowNewChatModal(false)}
        title="New Conversation"
        size="md"
      >
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          <p className="text-xs text-gray-500 mb-3">Select an agent to start a conversation:</p>
          {agents.map((agent) => {
            const hasConversation = conversations.some((c) => c.agentId === agent.id);
            return (
              <button
                key={agent.id}
                onClick={() => startNewConversation(agent)}
                className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-ldaja-blue/50 hover:bg-ldaja-blue/5 transition-colors text-left"
              >
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                  style={{ backgroundColor: agent.avatarColor || '#2563EB' }}
                >
                  {agent.name.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-ldaja-black">{agent.name}</span>
                    <span className={`w-2 h-2 rounded-full ${agentStatusColors[agent.status] || 'bg-gray-300'}`} />
                    <span className="text-[10px] text-gray-400">{agent.status}</span>
                  </div>
                  <p className="text-xs text-gray-500 truncate">{agent.role}</p>
                </div>
                {hasConversation && (
                  <span className="text-[10px] text-gray-400 flex-shrink-0">existing</span>
                )}
              </button>
            );
          })}
        </div>
      </Modal>
    </div>
  );
}

// ============================================================
// ConversationItem — Extracted for hover actions
// ============================================================

function ConversationItem({
  convo,
  agents,
  isSelected,
  onSelect,
  onArchive,
  onUnarchive,
  onDelete,
  formatTime,
  isArchived,
  hasApproval,
}: {
  convo: Conversation;
  agents: Agent[];
  isSelected: boolean;
  onSelect: () => void;
  onArchive?: () => void;
  onUnarchive?: () => void;
  onDelete: () => void;
  formatTime: (ts: string) => string;
  isArchived?: boolean;
  hasApproval?: boolean;
}) {
  const agent = agents.find((a) => a.id === convo.agentId);

  return (
    <div
      className={`relative group w-full text-left px-3 py-3 border-b border-gray-50 transition-colors cursor-pointer ${
        isSelected
          ? 'bg-ldaja-blue/5 border-l-2 border-l-ldaja-blue'
          : 'hover:bg-gray-50 border-l-2 border-l-transparent'
      } ${isArchived ? 'opacity-60' : ''}`}
      onClick={onSelect}
    >
      <div className="flex items-center gap-2.5">
        {/* Avatar — multi-avatar for collab, single for 1:1 */}
        {convo.isCollab ? (
          <div className="relative flex-shrink-0 w-8 h-8">
            {(convo.participants || []).slice(0, 2).map((pid, idx) => {
              const pa = agents.find((a) => a.id === pid);
              return (
                <div
                  key={pid}
                  className="absolute w-6 h-6 rounded-full flex items-center justify-center text-white text-[9px] font-bold border-2 border-white"
                  style={{
                    backgroundColor: pa?.avatarColor || '#6366f1',
                    top: idx === 0 ? 0 : 10,
                    left: idx === 0 ? 0 : 10,
                    zIndex: idx === 0 ? 2 : 1,
                  }}
                >
                  {pa?.name.charAt(0) || '?'}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="relative flex-shrink-0">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
              style={{ backgroundColor: agent?.avatarColor || '#2563EB' }}
            >
              {convo.agentName.charAt(0)}
            </div>
            {agent && !isArchived && (
              <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${agentStatusColors[agent.status] || 'bg-gray-400'}`} />
            )}
          </div>
        )}
        {/* Conversation Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-ldaja-black truncate flex items-center gap-1.5">
              {convo.agentName}
              {hasApproval && (
                <span className="relative flex h-2 w-2 flex-shrink-0" title="Pending approval">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
                </span>
              )}
            </span>
            <span className="text-[10px] text-gray-400 flex-shrink-0">
              {formatTime(convo.lastMessageTimestamp)}
            </span>
          </div>
          <p className="text-xs text-gray-500 truncate mt-0.5">
            {convo.lastMessage}
          </p>
        </div>
        {/* Unread Badge */}
        {convo.unreadCount > 0 && (
          <span className="flex-shrink-0 w-5 h-5 bg-ldaja-blue rounded-full flex items-center justify-center text-[10px] font-bold text-white">
            {convo.unreadCount}
          </span>
        )}
      </div>

      {/* Hover action buttons */}
      <div className="absolute right-2 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center gap-1 bg-white/90 backdrop-blur-sm rounded-md px-1 py-0.5 shadow-sm border border-gray-200">
        {isArchived && onUnarchive ? (
          <button
            onClick={(e) => { e.stopPropagation(); onUnarchive(); }}
            className="p-1 text-gray-400 hover:text-ldaja-blue rounded transition-colors"
            title="Unarchive"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
          </button>
        ) : onArchive ? (
          <button
            onClick={(e) => { e.stopPropagation(); onArchive(); }}
            className="p-1 text-gray-400 hover:text-ldaja-blue rounded transition-colors"
            title="Archive"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
          </button>
        ) : null}
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
          title="Delete"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    </div>
  );
}
